import { useCampaignReachOut } from "@entities/campaign-reach-out/lib/hooks/useCampaignReachOut";
import { api } from "@shared/lib/api";
import { ToastService } from "@shared/lib/services";
import { BackednResponse } from "@shared/lib/types";
import { getPlatformIcon } from "@shared/lib/utils/getPlatformIcon";
import {
  Avatar,
  Button,
  Col,
  Form,
  Input,
  Modal,
  Row,
  Space,
  Spin,
} from "antd";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import OnboardingEmailInfluencer from "./OnboardingEmailInfluencer";

interface UserData {
  name: string;
  username: string;
  platform: string;
  email_id?: string;
  profilePicUrl: string;
  link: string;
}

interface ChildFormProps {
  user: UserData;
  index: number;
  ref: React.Ref<{ validate: () => Promise<boolean> }>;
  onDelete: (index: number) => void;
  emailMap: Map<string, string>;
  loadingEmails: Set<string>;
}

const ChildForm = React.memo(
  React.forwardRef<
    { validate: () => Promise<boolean>; getValues: () => UserData },
    ChildFormProps
  >(({ user, index, onDelete, emailMap, loadingEmails }, ref) => {
    const [form] = Form.useForm();
    const emailKey = `${user.username}_${user.platform}`;
    const email = emailMap.get(emailKey) || user.email_id;
    const isLoading = loadingEmails.has(emailKey);

    useEffect(() => {
      if (email) {
        form.setFieldsValue({ email_id: email });
      }
    }, [email, form]);

    React.useImperativeHandle(
      ref,
      () => ({
        validate: async () => {
          try {
            await form.validateFields();
            return true;
          } catch {
            return false;
          }
        },
        getValues: () => {
          return { ...user, email_id: form.getFieldValue("email_id") };
        },
      }),
      [form, user]
    );

    return (
      <Form form={form} layout="vertical" initialValues={{ email_id: email }}>
        <Row gutter={16} align="middle">
          <Col flex={"auto"}>
            <Row align="middle" gutter={[8, 8]} style={{ cursor: "pointer" }}>
              <Col>
                <Avatar
                  src={user.profilePicUrl}
                  alt="Profile Picture"
                  size={40}
                />
              </Col>
              <Col>
                <div>{user?.name}</div>
                <Button
                  type="link"
                  style={{ padding: 0 }}
                  onClick={(e) => {
                    e.stopPropagation();
                    window.open(user?.link);
                  }}
                >
                  <Space>
                    {getPlatformIcon(user.platform)}
                    {user?.username}
                  </Space>
                </Button>
              </Col>
            </Row>
          </Col>

          <Col flex="300px">
            <Form.Item
              label="Email ID"
              name="email_id"
              rules={[{ required: true, message: "Please enter an email ID!" }]}
            >
              {isLoading ? (
                <Spin size="small">
                  <Input disabled placeholder="Fetching email..." />
                </Spin>
              ) : (
                <Input placeholder="Enter your email" />
              )}
            </Form.Item>
          </Col>
          <Col>
            <Button danger onClick={() => onDelete(index)}>
              Delete
            </Button>
          </Col>
        </Row>
      </Form>
    );
  })
);

const CampaignReachoutInfluencerModal: React.FC<{
  visible: boolean;
  onClose: () => void;
  userData: UserData[];
}> = ({ visible, onClose, userData }) => {
  const [users, setUsers] = useState<UserData[]>(userData);
  const [emailMap, setEmailMap] = useState<Map<string, string>>(new Map());
  const [loadingEmails, setLoadingEmails] = useState<Set<string>>(new Set());
  const [isFetchingEmails, setIsFetchingEmails] = useState<boolean>(false);

  const childFormRefs = useRef<
    Map<
      number,
      React.RefObject<{
        validate: () => Promise<boolean>;
        getValues: () => UserData;
      } | null>
    >
  >(new Map());

  const [showEmailForm, setShowEmailForm] = useState<boolean>(false);

  const {
    update: reachOutInfluencers,
    isUpdating: isReachOutInfluencersUpdating,
    isUpdated: isReachOutInfluencersUpdated,
  } = useCampaignReachOut();

  useEffect(() => {
    if (isReachOutInfluencersUpdated) {
      onClose();
      setShowEmailForm(false);
    }
  }, [isReachOutInfluencersUpdated, onClose]);

  useEffect(() => {
    childFormRefs.current = new Map();
    setLoadingEmails(new Set());
    setUsers(userData);
  }, [userData]);

  // Batch fetch emails in chunks to avoid overwhelming the browser
  const fetchEmailsBatch = useCallback(async (userBatch: UserData[]) => {
    if (userBatch.length === 0) {
      return; // No users to fetch
    }

    const batchSize = 10; // Process 10 at a time

    for (let i = 0; i < userBatch.length; i += batchSize) {
      const batch = userBatch.slice(i, i + batchSize);
      const promises = batch.map(async (user) => {
        const emailKey = `${user.username}_${user.platform}`;

        setLoadingEmails((prev) => new Set(prev).add(emailKey));

        try {
          const response = (await api
            .get(
              `social-media/contact/handle?username=${user.username}&platform=${user.platform}`
            )
            .json()) as BackednResponse<{
            isOnboarded: boolean;
            emailId: Array<string>;
            phoneNumber: Array<string>;
            countryCode: Array<string>;
          }>;

          const email = response.data.emailId?.[0] || "";

          setEmailMap((prev) => {
            // Check again before setting to avoid overwriting
            if (prev.has(emailKey)) {
              return prev;
            }
            const newMap = new Map(prev);
            newMap.set(emailKey, email);
            return newMap;
          });
        } catch (error) {
          console.error(`Error fetching email for ${user.username}`, error);
        } finally {
          setLoadingEmails((prev) => {
            const newSet = new Set(prev);
            newSet.delete(emailKey);
            return newSet;
          });
        }
      });

      // Wait for current batch to complete before starting next batch
      await Promise.all(promises);

      // Small delay to prevent UI blocking
      if (i + batchSize < userBatch.length) {
        await new Promise((resolve) => setTimeout(resolve, 100));
      }
    }
  }, []);

  // Fetch emails when modal opens or users change
  useEffect(() => {
    if (visible && users.length > 0 && !isFetchingEmails) {
      setIsFetchingEmails(true);

      // Get users that don't have emails yet (check both initial email_id and cached emailMap)
      const usersWithoutEmails = users.filter((user) => {
        const emailKey = `${user.username}_${user.platform}`;
        // Skip if user already has email_id OR if we've already cached it in emailMap
        return !user.email_id && !emailMap.has(emailKey);
      });

      if (usersWithoutEmails.length > 0) {
        fetchEmailsBatch(usersWithoutEmails).finally(() => {
          setIsFetchingEmails(false);
        });
      } else {
        setIsFetchingEmails(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, users.length, fetchEmailsBatch]);

  // Ensure refs exist for all users
  useEffect(() => {
    users.forEach((_, index) => {
      if (!childFormRefs.current.has(index)) {
        childFormRefs.current.set(index, React.createRef());
      }
    });
  }, [users.length]);

  const handleDelete = useCallback((index: number) => {
    setUsers((prevUsers) => {
      const newUsers = prevUsers.filter((_, i) => i !== index);
      // Clean up refs for deleted items
      childFormRefs.current.delete(index);
      // Reindex remaining refs
      const newRefs = new Map();
      newUsers.forEach((_, newIndex) => {
        const oldIndex = prevUsers.findIndex((u) => {
          return u === newUsers[newIndex];
        });
        if (oldIndex >= 0 && childFormRefs.current.has(oldIndex)) {
          newRefs.set(newIndex, childFormRefs.current.get(oldIndex));
        } else {
          newRefs.set(newIndex, React.createRef());
        }
      });
      childFormRefs.current = newRefs;
      return newUsers;
    });
  }, []);

  const handleOk = useCallback(async () => {
    // Validate forms in batches to avoid blocking
    const batchSize = 20;
    const refsArray = Array.from(childFormRefs.current.entries())
      .sort(([a], [b]) => a - b)
      .map(([, ref]) => ref);

    for (let i = 0; i < refsArray.length; i += batchSize) {
      const batch = refsArray.slice(i, i + batchSize);
      const batchResults = await Promise.all(
        batch.map(async (ref) => {
          return ref.current ? ref.current.validate() : false;
        })
      );

      if (!batchResults.every((valid) => valid)) {
        ToastService.error("Some fields are invalid.");
        return;
      }
    }

    setShowEmailForm(true);
  }, []);

  // Memoize user forms to prevent unnecessary re-renders
  const userForms = useMemo(
    () =>
      users.map((user, index) => {
        if (!childFormRefs.current.has(index)) {
          childFormRefs.current.set(index, React.createRef());
        }
        return (
          <div
            key={`${user.username}_${user.platform}_${index}`}
            style={{ marginBottom: 16 }}
          >
            <ChildForm
              user={user}
              index={index}
              ref={childFormRefs.current.get(index)!}
              onDelete={handleDelete}
              emailMap={emailMap}
              loadingEmails={loadingEmails}
            />
          </div>
        );
      }),
    [users, emailMap, loadingEmails, handleDelete]
  );

  return (
    <Modal
      key={visible ? "open" : "closed"}
      title={`Onboarding Influencer (${users.length})`}
      open={visible}
      onCancel={onClose}
      footer={[
        <Button key="cancel" onClick={onClose}>
          Cancel
        </Button>,
        <Button
          key="submit"
          type="primary"
          onClick={handleOk}
          loading={isReachOutInfluencersUpdating}
          disabled={isReachOutInfluencersUpdating || isFetchingEmails}
        >
          {isFetchingEmails ? "Loading emails..." : "Submit"}
        </Button>,
      ]}
      width={1000}
    >
      <div
        style={{
          maxHeight: 500,
          overflowY: "auto",
          paddingRight: 8,
        }}
      >
        {isFetchingEmails && users.length > 0 && (
          <div style={{ marginBottom: 16, textAlign: "center" }}>
            <Spin size="small" /> Loading emails...
          </div>
        )}
        {userForms}
      </div>

      <OnboardingEmailInfluencer
        visible={showEmailForm}
        onClose={() => {
          setShowEmailForm(false);
        }}
        onSubmit={(values) => {
          const refsArray = Array.from(childFormRefs.current.entries())
            .sort(([a], [b]) => a - b)
            .map(([, ref]) => ref);

          const formValues: UserData[] = refsArray
            .map((ref) => ref?.current?.getValues())
            .filter((ele): ele is UserData => Boolean(ele));

          reachOutInfluencers({
            brandName: values.brandName,
            briefName: values.briefName,
            deliverables: values.deliverables,
            timeline: values.timeline,
            socialMedias: formValues.map((ele) => ({
              emailId: ele.email_id || "",
              isUserEntered: true,
              platform: ele.platform,
              username: ele.username,
            })),
          });
        }}
      />
    </Modal>
  );
};

export default CampaignReachoutInfluencerModal;
